import * as React from 'react'

import {
  AttendanceCycle,
  AttendancePeriod,
  AttendancePeriodsDocument,
  TimeTrackingClockInDayRowWorkedBreaksFragment,
  TimeTrackingClockInPageDocument,
  TimesheetBalanceFragment,
  TimesheetCompanySettingsVersionHistoryFragment,
  TimesheetCyclesDocument,
  TimesheetEditTimesheetRequestFragment,
  TimesheetEstimatedTimeFragment,
  TimesheetFeaturesDocument,
  TimesheetOpenShiftFragment,
  TimesheetPageCycleFragment,
  TimesheetPageEmployeesSettingFragment,
  TimesheetPageShiftFragment,
  TimesheetReviewStatsDocument,
  TimesheetReviewsStatsFragment,
  TimesheetSettingsDocument,
  TimesheetWorkedTimeFragment,
} from 'generated/resources/graphql'
import _ from 'lodash'
import { Dictionary, groupBy } from 'lodash'

import { useEffect } from 'modules/core/hooks/useEffect'
import { useQuery } from 'modules/core/hooks/useQuery'
import useSubscribe from 'modules/core/hooks/useSubscribe'
import { i18nDateToString, stringToDate } from 'modules/core/lib/dates'
import { EventTypes } from 'modules/core/lib/pubSub'

import { OvertimePoolsCollection } from 'modules/attendance/hooks/OvertimePoolsCollection'
import { BreakConfiguration } from 'modules/attendance/hooks/useBreakConfigurationsByIds'
import { useBreakConfigurationsByIds } from 'modules/attendance/hooks/useBreakConfigurationsByIds'
import { useFetchBankOfHours } from 'modules/attendance/hooks/useFetchBankOfHours'
import { useFetchCompensations } from 'modules/attendance/hooks/useFetchCompensations'
import ExtraHoursUtils from 'modules/attendance/lib/utils/ExtraHoursUtils'
import { TimeBlocksBalanceCollection } from 'modules/attendance/store/models/TimeBlocksBalanceCollection'

import { useAttendanceDateNavigator } from './attendanceDateNavigatorContext'
import { TimesheetFeatures } from './timesheetFeatures'
import AttendanceCyclesUtils from './utils/AttendanceCycles'
import AttendanceWorkedTimeUtils, {
  AggregatedWorkedTimeBlocks,
} from './utils/AttendanceWorkedTimeUtils'
import Balance from './utils/Balance'
import EmployeesSettingsUtils from './utils/EmployeesSettingsUtils'

interface ContextProps {
  loading: boolean
  shifts: TimesheetPageShiftFragment[]
  workedBreaks: TimeTrackingClockInDayRowWorkedBreaksFragment[]
  estimatedTimes: TimesheetEstimatedTimeFragment[]
  openShift?: TimesheetOpenShiftFragment | null
  balances: TimesheetBalanceFragment[]
  cycles: TimesheetPageCycleFragment[]
  employeesSettings: TimesheetPageEmployeesSettingFragment[]
  currentEmployeesSetting: TimesheetPageEmployeesSettingFragment | undefined
  editTimesheetRequests: TimesheetEditTimesheetRequestFragment[]
  attendanceEmployeesPolicyId?: number | null
  workedTimes: TimesheetWorkedTimeFragment[]
  aggregatedWorkedTime?: AggregatedWorkedTimeBlocks | null
  companySettingsVersionHistories: TimesheetCompanySettingsVersionHistoryFragment[]
  reviewsStats: TimesheetReviewsStatsFragment | null
  overtimePools: OvertimePoolsCollection
  timeBlocksBalance: TimeBlocksBalanceCollection
  bankOfHours: number
  openPeriod: boolean
  lastCycle: Pick<AttendanceCycle, 'startDate' | 'endDate'> | undefined
  featureByName: Dictionary<
    {
      id: string
      enabled: boolean
      name: string
    }[]
  >
  employeeId: number
  attendancePeriods: AttendancePeriod[] | undefined
  breakConfigurationsByIds: Record<number, BreakConfiguration>
  extraHoursMinutes: number
}

const Context = React.createContext<ContextProps>({
  loading: false,
  workedBreaks: [],
  estimatedTimes: [],
  balances: [],
  cycles: [],
  employeesSettings: [],
  editTimesheetRequests: [],
  companySettingsVersionHistories: [],
  shifts: [],
  workedTimes: [],
  reviewsStats: null,
  overtimePools: new OvertimePoolsCollection([]),
  timeBlocksBalance: new TimeBlocksBalanceCollection(undefined),
  bankOfHours: 0,
  openPeriod: false,
  lastCycle: undefined,
  featureByName: {},
  employeeId: 0,
  attendancePeriods: [],
  currentEmployeesSetting: undefined,
  breakConfigurationsByIds: {},
  extraHoursMinutes: 0,
})

export const TimesheetDataProvider = ({
  employeeId,
  companyId,
  setLoading,
  children,
}: {
  employeeId: number
  companyId: number
  setLoading: (loading: boolean) => void
  children: React.ReactNode
}) => {
  const { stringDateFrom: startOn, stringDateTo: endOn } = useAttendanceDateNavigator()

  const featureIds = Object.values(TimesheetFeatures)

  const { loading: loadingFeatures, data: featuresData } = useQuery(TimesheetFeaturesDocument, {
    variables: { featureNames: featureIds, companyId },
    fetchPolicy: 'cache-first',
  })

  const features = featuresData?.features.featuresConnection.nodes ?? []
  const featureByName = groupBy(features, (feature) => feature.name)
  const hasBankOfHours = TimesheetFeatures.BANK_OF_HOURS in featureByName
  const hasBankOfHoursV2 = TimesheetFeatures.BANK_OF_HOURS_V2 in featureByName
  const shouldUseBankOfHoursV2Flow = hasBankOfHours && hasBankOfHoursV2
  const hasCompensationV2 = TimesheetFeatures.COMPENSATION_FLOW_V2 in featureByName

  const variables = {
    employeeId,
    startOn,
    endOn,
  }

  const { data: settingsData, loading: loadingSettings } = useQuery(TimesheetSettingsDocument, {
    variables,
  })

  const employeesSettings = settingsData?.attendance?.employeesSettingsConnection.nodes ?? []

  const balancePayrollCompensationEnabled =
    EmployeesSettingsUtils.hasBalancePayrollCompensationEnabled(employeesSettings)

  const { loading: loadingCycles, data: cyclesData } = useQuery(TimesheetCyclesDocument, {
    variables,
    skip: !hasBankOfHours,
  })

  const todayDate = new Date()
  const today = i18nDateToString(todayDate, 'backend')
  const yesterdayDate = new Date(todayDate)
  yesterdayDate.setDate(todayDate.getDate() - 1)
  const yesterday = i18nDateToString(yesterdayDate, 'backend')

  const { loading: loadingCurrentCycle, data: currentCycleData } = useQuery(
    TimesheetCyclesDocument,
    {
      variables: { employeeId, startOn: today, endOn: today },
      skip: !shouldUseBankOfHoursV2Flow,
    }
  )

  const cycles = cyclesData?.attendance.cyclesConnection.nodes ?? []
  const currentCycles = currentCycleData?.attendance?.cyclesConnection.nodes ?? []

  let lastCycle = AttendanceCyclesUtils.lastCycle(cycles)
  let periodStartDate = lastCycle?.startDate ?? startOn
  let periodEndDate = lastCycle?.endDate ?? endOn
  let openPeriod = AttendanceCyclesUtils.openPeriod(cycles)

  if (shouldUseBankOfHoursV2Flow) {
    const lastCycleCandidate = AttendanceCyclesUtils.lastCycle(currentCycles)
    const fallbackLastCycle = AttendanceCyclesUtils.lastCycle(cycles)
    lastCycle = lastCycleCandidate ?? fallbackLastCycle

    const lastCycleStartDate = lastCycle?.startDate
    const lastCycleEndDate = lastCycle?.endDate

    periodStartDate = lastCycleStartDate ?? startOn
    periodEndDate = lastCycleEndDate ?? endOn
    openPeriod = lastCycle
      ? AttendanceCyclesUtils.openPeriod([lastCycle])
      : AttendanceCyclesUtils.openPeriod(cycles)
  }

  const { bankOfHours } = useFetchBankOfHours({
    employeeId,
    cycleStartOn: periodStartDate,
    cycleEndOn: shouldUseBankOfHoursV2Flow ? yesterday : periodEndDate,
    skip: !hasBankOfHours || (shouldUseBankOfHoursV2Flow && loadingCurrentCycle),
  })

  const { loading: loadingClockInPage, data: clockinData } = useQuery(
    TimeTrackingClockInPageDocument,
    {
      variables: {
        ...variables,
        companyId,
        editTimesheetRequestEnabled:
          EmployeesSettingsUtils.hasEditTimesheetAuthorization(employeesSettings),
      },
      skip: loadingSettings,
    }
  )

  const {
    attendanceBalancesConnection,
    attendanceEditTimesheetRequests = [],
    attendanceEmployeesPolicyId,
    attendanceEstimatedTimesConnection,
    attendanceShiftsConnection,
    attendanceWorkedBreaksConnection,
    attendanceWorkedTimesConnection,
    attendanceAggregatedWorkedTime,
    openShift,
  } = clockinData?.attendance?.employee ?? {}

  const aggregatedTimeBlocksBalance = Balance.aggregateBalanceByAccumulation(
    attendanceBalancesConnection?.nodes ?? [],
    employeeId
  )

  const timeBlock = aggregatedTimeBlocksBalance.at(-1)

  const {
    compensations,
    loading: loadingCompensations,
    refetch: refetchCompensations,
  } = useFetchCompensations(
    {
      employeeIds: [variables.employeeId],
      startOn: timeBlock?.startOn ?? variables.startOn,
      endOn: timeBlock?.endOn ?? variables.endOn,
    },
    { skip: !timeBlock || hasCompensationV2 }
  )

  const { data: periods, loading: loadingPeriods } = useQuery(AttendancePeriodsDocument, {
    variables: {
      employeeIds: [employeeId],
      startOn,
      endOn,
    },
  })
  const attendancePeriods = periods?.attendance?.periodsConnection.nodes ?? []

  const { data: reviewStatsData, loading: reviewStatsLoading } = useQuery(
    TimesheetReviewStatsDocument,
    {
      variables,
    }
  )
  const reviewsStats = reviewStatsData?.attendance?.reviewsStatsConnection?.nodes ?? []

  const overtimePools = clockinData?.attendance?.overtimePoolsConnection?.nodes ?? []

  useSubscribe([EventTypes.ATTENDANCE_SHIFT_SAVED, EventTypes.ATTENDANCE_SHIFT_TOGGLED], () => {
    if (!hasCompensationV2) {
      refetchCompensations()
    }
  })

  const currentEmployeesSetting = EmployeesSettingsUtils.currentSettingWithinDate(
    employeesSettings,
    stringToDate(startOn, 'backend')
  )

  useEffect(() => {
    setLoading(loadingClockInPage || loadingSettings)
  }, [loadingClockInPage, loadingSettings]) // eslint-disable-line react-hooks/exhaustive-deps

  const aggregatedWorkedTime = AttendanceWorkedTimeUtils.aggregatedWorkedTimeBlocks(
    attendanceWorkedTimesConnection?.nodes ?? [],
    attendanceAggregatedWorkedTime ?? null
  )

  const timeSettingsBreakConfigurationIds = aggregatedWorkedTime
    ? _(aggregatedWorkedTime.workedTimeBlocks)
        .map((block) => block.timeSettingsBreakConfigurationId)
        .compact()
        .uniq()
        .sortedUniq()
        .value()
    : []

  const { loading: loadingBreakConfigurations, breakConfigurationsByIds } =
    useBreakConfigurationsByIds(timeSettingsBreakConfigurationIds)

  const { companySettingsVersionHistoriesConnection } = clockinData?.attendance ?? {}

  const loading =
    loadingSettings ||
    loadingClockInPage ||
    loadingCompensations ||
    reviewStatsLoading ||
    loadingCycles ||
    (shouldUseBankOfHoursV2Flow && loadingCurrentCycle) ||
    loadingFeatures ||
    loadingPeriods ||
    loadingBreakConfigurations

  const extraHoursMinutes = ExtraHoursUtils.sumExtraHoursWithinRange({
    balances: attendanceBalancesConnection?.nodes ?? [],
  })

  const returnVariables = {
    loading,
    workedBreaks: attendanceWorkedBreaksConnection?.nodes ?? [],
    estimatedTimes: attendanceEstimatedTimesConnection?.nodes ?? [],
    openShift,
    balances: attendanceBalancesConnection?.nodes ?? [],
    cycles: cyclesData?.attendance?.cyclesConnection.nodes ?? [],
    workedTimes: attendanceWorkedTimesConnection?.nodes ?? [],
    aggregatedWorkedTime,
    breakConfigurationsByIds,
    editTimesheetRequests: attendanceEditTimesheetRequests ?? [],
    employeesSettings,
    attendanceEmployeesPolicyId,
    companySettingsVersionHistories: companySettingsVersionHistoriesConnection?.nodes ?? [],
    shifts: attendanceShiftsConnection?.nodes ?? [],
    reviewsStats: reviewsStats[0] ?? null,
    overtimePools: new OvertimePoolsCollection(overtimePools),
    timeBlocksBalance: new TimeBlocksBalanceCollection(
      timeBlock,
      compensations,
      balancePayrollCompensationEnabled
    ),
    bankOfHours,
    openPeriod,
    lastCycle,
    featureByName,
    employeeId,
    attendancePeriods,
    currentEmployeesSetting,
    extraHoursMinutes,
  }

  return <Context.Provider value={returnVariables}>{children}</Context.Provider>
}

export const useTimesheetData = (): ContextProps => {
  const TimesheetData = React.useContext(Context)

  if (!TimesheetData) {
    throw new Error('useTimesheetData must be used within an TimesheetDataProvider')
  }

  return TimesheetData
}
